<script setup lang="ts">
  import {ref, watch, nextTick} from 'vue';
  import {useI18n} from 'vue-i18n';
  import useCpBem from '~/composables/useCpBem';
  import CpAvatar from '~/components/cp/CpAvatar/CpAvatar.vue';
  import CpPillPdp from '~/components/cp/CpPill/CpPillPdp/CpPillPdp.vue';
  import CpProductRate from '~/components/cp/CpProductRate/CpProductRate.vue';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import CpButtonIcon from '~/components/cp/CpButtonIcon/CpButtonIcon.vue';
  import CpThumbRate from '~/components/cp/CpThumb/CpThumbRate/CpThumbRate.vue';
  import CpModalReportUcg from '~/components/cp/CpModal/CpModalReportUcg/CpModalReportUcg.vue';
  import CpModalActions from '~/components/cp/CpModal/CpModalActions/CpModalActions.vue';
  import CpModalDialog from '~/components/cp/CpModal/CpModalDialog/CpModalDialog.vue';
  import type {ReviewViewModel} from '~/composables/queries/useReviews';

  const props = withDefaults(defineProps<{
    /** Prop to identify every single card */
    id: string,
    /** Define the number of rate stars */
    rate: number,
    /** Avatar image */
    image?: string,
    /** indicates that the card lives on PDP */
    pdp: boolean,
    /** Title for the review */
    title: string,
    /** Body Text */
    body: string,
    userName: string,
    /** Date the review was written */
    date: string,
    /** show or hide verified purchase pill  */
    verified: boolean,
    /** renders full border for the card, if is false only renders bottom border */
    allReviews?: boolean,
    /** if show border bottom */
    showBorderBottom?: boolean,
    /** number of dislikes for the review */
    dislikesCount: number,
    /** number of likes for the review */
    likesCount: number,
    /** change styles to AF */
    af?: boolean,

    reviewData?: ReviewViewModel
    isLogged?: boolean
  }>(), {
    showBorderBottom: true,
    af: false,
  });

  const {t} = useI18n();
  const {b, bm, e, em} = useCpBem('cp-card-review');


  const showModalReport = ref<boolean>(false);
  const showModalReportForm = ref<boolean>(false);
  const success = ref<boolean>(false);
  const reportReason = ref<string>('');
  const loading = ref<boolean>(false);
  const showModalRequiredLogin = ref<boolean>(false);
  const reportType = ref<'question'|'answer'|'review'>('question');
  const dataToReport = ref();
  const redirectTo = ref<'report'>('report');
  const likesCountRef = ref<number>(props.likesCount);
  const dislikesCountRef = ref<number>(props.dislikesCount);
  const kindRate = ref<'enabled'|'votedThumbsUp'|'votedThumbsDown'|'disabled'>(props.pdp ? 'enabled': 'disabled');
  const voted = ref<boolean>(false);
  const generateVote = ref<boolean>(false);
  const votedValue = ref<number>(0);

  const setVote = async (vote: number, addVote = false) => {
    await nextTick();
    if (!props.isLogged || !props.reviewData) {
      await nextTick();
      voted.value = false;
      generateVote.value = true;
      votedValue.value = vote;
      if (vote === 1) {
        likesCountRef.value--;
      } else {
        dislikesCountRef.value--;
      }
      showModalRequiredLogin.value = true;
      return;
    }
    kindRate.value = vote === 1 ? 'votedThumbsUp' : 'votedThumbsDown';
    const {error} = await useReviewVote({
      productId: props.reviewData.articleId,
      reviewId: props.reviewData.id,
      vote,
    });

    if (error.value) {
      kindRate.value = 'enabled';
    }

    if (error.value && !addVote) {
      if (vote === 1) {
        likesCountRef.value--;
      } else {
        dislikesCountRef.value--;
      }
      return;
    } else if (!error.value && addVote) {
      if (vote === 1) {
        likesCountRef.value++;
      } else {
        dislikesCountRef.value++;
      }
    }
  };

  const modalReport = () => {
    reportType.value = 'review';
    dataToReport.value = props.reviewData;
    showModalReport.value = true;
  };

  const report = () => {
    if (props.isLogged) {
      modalReportForm();
    } else {
      showModalReport.value = false;
      showModalRequiredLogin.value = true;
    }
  };

  const modalReportForm = () => {
    showModalReport.value = false;
    showModalReportForm.value = true;
  };


  const sendReport = async () => {
    success.value = false;
    loading.value = true;
    try {
      useReportContent({
        type: reportType.value,
        data: dataToReport.value,
        reason: reportReason.value,
        user: '',
      });
    } catch {}

    success.value = true;
    loading.value = false;
    dataToReport.value = null;
  };

  const showModalLogin = () => {
    showModalRequiredLogin.value = false;
    redirectTo.value = 'report';
    try {
      useEvent('show-login-modal', {show: true});
    } catch {}
  };

  watch(props, () => {
    if (props.isLogged && dataToReport.value) {
      if (redirectTo.value === 'report') {
        showModalReportForm.value = true;
      }
    } else if (props.isLogged && generateVote.value) {
      generateVote.value = false;
      setVote(votedValue.value, true);
    }
  });
</script>

<template>
    <div :class="b">
        <div
            :class="em('container',{
                'full-border':allReviews,
                'show-border-bottom': showBorderBottom && !af,
                af: af && showBorderBottom
            })"
        >
            <div>
                <CpAvatar size="small" :image="image" :alt="title" />
            </div>
            <div :class="e('right-column')">
                <div v-if="verified" :class="em('top-row', { 'verified': !verified })">
                    <CpPillPdp
                        :class="e('pill')"
                        kind="reviews"
                        variant="primary"
                        label-kind="white"
                        :label="t('verified')"
                        :border="'borderPrimary'"
                        :icon="af ? 'verified' : undefined"
                    />
                </div>
                <CpButtonIcon
                    v-if="pdp"
                    :class="e('actions')"
                    kind="actions"
                    type="primary"
                    @click="modalReport"
                />
                <CpProductRate :rate="rate" hide-count :total-reviews="likesCount" :af="af" />
                <CpText variant="body-bold" :type="af ? 'white' : 'neutral-dark'">
                    {{ title }}
                </CpText>
                <CpText variant="body-regular" :type="af ? 'white' : 'neutral-dark'">
                    {{ body }}
                </CpText>
                <div :class="e('bottom-row')">
                    <div :class="e('info-row')">
                        <CpText variant="caption-bold" :type="af ? 'white' : 'neutral-dark'">
                            {{ userName }}
                        </CpText>
                        <CpText
                            :class="e('date')"
                            variant="caption-regular"
                            :type="af ? 'neutral-extra-light':'neutral'"
                        >
                            {{ date }}
                        </CpText>
                    </div>
                    <div :class="e('rate-thumb')">
                        <CpThumbRate
                            v-model:like-value="likesCountRef"
                            v-model:dislike-value="dislikesCountRef"
                            v-model:kind="kindRate"
                            v-model:voted="voted"
                            @thumb-down="setVote(0)"
                            @thumb-up="setVote(1)"
                        />
                    </div>
                </div>
            </div>
        </div>
        <CpModalDialog
            v-model:show="showModalRequiredLogin"
            :title="t('warning')"
            :body="t('forYouCanWrite')"
            :label-first-button="t('logIn')"
            type-first-button="primary"
            @first-button="showModalLogin"
        />

        <CpModalReportUcg
            v-if="showModalReportForm"
            v-model:show="showModalReportForm"
            :success="success"
            @update:value="reportReason = $event"
            @send-report="sendReport"
            @on-close="success = false"
        />
        <CpModalActions
            v-model:show="showModalReport"
            with-picture
            type="reportUcg"
            @report-comment="report"
        />
    </div>
</template>

<style scoped lang="scss">
.cp-card-review {

  &__container {
    position: relative;
    padding: $cp-gutter;
    display: flex;
    gap: $cp-gutter;

    &--full-border {
      border: $cp-border-standard;
      border-radius: $cp-gutter-small;
    }

    &--show-border-bottom {
      border-bottom: $cp-border-standard;
    }

    &--af {
      border-bottom: 1px solid var(--color-neutral-light);
    }
  }

  &__top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 20px;

    &--verified {
      justify-content: flex-end;
    }
  }

  &__pill {
    height: 20px;
  }

  &__right-column {
    display: flex;
    flex-direction: column;
    gap: $cp-gutter-small;
    flex: 1;
  }

  &__bottom-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: $cp-gutter-xsmall;
  }

  &__info-row {
    display: flex;
    gap: $cp-gutter-xsmall;
    align-items: center;
  }

  &__like-count {
    display: flex;
    gap: $cp-gutter-xsmall;
    justify-content: center;
    align-items: center;
  }

  &__rate-thumb {
    display: flex;
    gap: $cp-gutter-small;
  }

  &__actions {
    position: absolute;
    right: $cp-gutter;
  }

  &__date {
    text-wrap: nowrap;
  }
}
</style>

<i18n locale="es-US" lang="yaml">
  verified: "Compra verificada"
  forYouCanWrite: 'Para que puedas votar una opinión es necesario que inicies sesión.'
  warning: 'Aviso'
  logIn: "Ingresar"
</i18n>
